import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";
import generateVideoThumbnail from "../utils/generateVideoThumbnails";
import type { ChatDraftAttachment, ChatDraftKind } from "./chatAttachmentTypes";

interface FileContextType {
  selectedFile: File | null;
  setSelectedFile: (file: File | null) => void;
  chatAttachments: ChatDraftAttachment[];
  addChatAttachment: (file: File, kind: ChatDraftKind) => void;
  removeChatAttachment: (id: string) => void;
  clearChatAttachments: () => void;
  updateChatAttachment: (
    id: string,
    patch: Partial<Pick<ChatDraftAttachment, "videoThumbnail">>,
  ) => void;
}

const FileContext = createContext<FileContextType | undefined>(undefined);

export const useFile = (): FileContextType => {
  const context = useContext(FileContext);
  if (!context) {
    throw new Error("useFile must be used within a FileProvider");
  }
  return context;
};

interface FileProviderProps {
  children: ReactNode;
}

export const FileProvider: React.FC<FileProviderProps> = ({ children }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [chatAttachments, setChatAttachments] = useState<ChatDraftAttachment[]>(
    [],
  );

  const removeChatAttachment = useCallback((id: string) => {
    setChatAttachments((prev) => {
      const att = prev.find((a) => a.id === id);
      if (att?.previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(att.previewUrl);
      }
      return prev.filter((a) => a.id !== id);
    });
  }, []);

  const clearChatAttachments = useCallback(() => {
    setChatAttachments((prev) => {
      prev.forEach((a) => {
        if (a.previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(a.previewUrl);
        }
      });
      return [];
    });
  }, []);

  const updateChatAttachment = useCallback(
    (id: string, patch: Partial<Pick<ChatDraftAttachment, "videoThumbnail">>) => {
      setChatAttachments((prev) =>
        prev.map((a) => (a.id === id ? { ...a, ...patch } : a)),
      );
    },
    [],
  );

  const addChatAttachment = useCallback((file: File, kind: ChatDraftKind) => {
    const id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const previewUrl = URL.createObjectURL(file);
    setChatAttachments((prev) => [
      ...prev,
      { id, kind, file, previewUrl, fileName: file.name },
    ]);
    if (kind === "video") {
      generateVideoThumbnail(file, 1, 20).then((thumb: string) => {
        updateChatAttachment(id, { videoThumbnail: thumb });
      });
    }
  }, [updateChatAttachment]);

  return (
    <FileContext.Provider
      value={{
        selectedFile,
        setSelectedFile,
        chatAttachments,
        addChatAttachment,
        removeChatAttachment,
        clearChatAttachments,
        updateChatAttachment,
      }}
    >
      {children}
    </FileContext.Provider>
  );
};
